import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete, Box, Button, Collapse, FormControl, IconButton, InputLabel,
  MenuItem, Select, TextField, Tooltip, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Helpers from './helpers';

/**
 * Editor for data_stream.partition_configs.
 *
 * The owner list is restricted to this parent's children — an entry naming a non-child
 * resolves to nothing and the split silently fails. anyOwner lifts that for iYotah.
 *
 * Every legacy shape parses and is rewritten to the explicit form on save. An unparseable
 * value falls back to raw JSON rather than being discarded.
 */
const PartitionConfigsEditor = ({value, onChange, childOwners, owners, anyOwner, providerScheme, error, helperText}) => {
  const [showJson, setShowJson] = useState(false);

  const {ok, rows} = Helpers.parsePartitionConfigs(value);

  // Offer the provider's own schemes, reassign (an override, always available), and any
  // type already stored — dropping the last would make an existing rule uneditable.
  // Most providers name one scheme; EZfeed-Loads names PEN and LABEL, and its streams
  // need rules of both kinds on the one stream.
  const schemeMatches = Helpers.schemeMatchTypes(providerScheme);
  const matchOptions = schemeMatches.length
    ? [...new Set([...schemeMatches, 'reassign', ...rows.map((r) => r.match)])]
    : Helpers.MATCH_TYPES;
  const defaultMatch = schemeMatches[0] || 'pen';

  const label = (id, owner) => (owner ? `${id} ${owner.name}` : String(id));

  // The any-owner list is long enough to need sorting; children stay in association order.
  const ownerOptions = anyOwner
    ? [...owners]
      .sort((a, b) => String(a.name).localeCompare(String(b.name)))
      .map((owner) => ({id: owner.id, label: label(owner.id, owner)}))
    : childOwners.map((association) => {
      const id = association.child_owner_id;
      return {id, label: label(id, owners.find((o) => o.id === id))};
    });

  const emit = (nextRows) => onChange(Helpers.serializePartitionConfigs(nextRows));

  const updateRow = (index, patch) =>
    emit(rows.map((row, i) => (i === index ? {...row, ...patch} : row)));

  // No default out of the full owner list — an arbitrary one would save unnoticed.
  const addRow = () =>
    emit([...rows, {
      match: defaultMatch,
      value: '',
      owner_id: anyOwner ? '' : (ownerOptions[0]?.id ?? ''),
    }]);

  const removeRow = (index) => emit(rows.filter((_, i) => i !== index));

  // An unparseable value keeps its raw text: rendering rows would mean throwing it away.
  if (!ok) {
    return (
      <Box>
        <Typography variant='body2' color='error' sx={{mb: 1}}>
          This value could not be read as partition configs. Edit it as JSON below.
        </Typography>
        <TextField
          name='partition_configs'
          label='Partition Configs'
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
          onChange={(e) => onChange(e.target.value)}
          error={Boolean(error)}
          helperText={helperText}
          fullWidth
          multiline
          rows={6}
          variant='outlined'
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
        <Typography variant='subtitle2'>
          Partition Configs
          {schemeMatches.length > 0 && (
            <Typography component='span' variant='body2' color='textSecondary' sx={{ml: 1}}>
              this provider splits by{' '}
              {schemeMatches
                .map((match) => Helpers.MATCH_LABELS[match].toLowerCase())
                .join(' and ')}
            </Typography>
          )}
        </Typography>
        <Button
          size='small'
          startIcon={<AddIcon/>}
          onClick={addRow}
          disabled={ownerOptions.length === 0}
        >
          Add rule
        </Button>
      </Box>

      {anyOwner && (
        <Typography variant='body2' color='textSecondary' sx={{mb: 1}}>
          This stream belongs to iYotah, so its rules may name any owner. Rows matching no
          rule stay with iYotah and are dropped downstream — give every file a rule.
        </Typography>
      )}

      {ownerOptions.length === 0 && (
        <Typography variant='body2' color='textSecondary' sx={{mb: 1}}>
          No child owners are associated with this owner, so a rule would have nowhere to
          send rows. Add the association first, then come back to add rules.
        </Typography>
      )}

      {rows.length === 0 && ownerOptions.length > 0 && !anyOwner && (
        <Typography variant='body2' color='textSecondary' sx={{mb: 1}}>
          No rules. Rows stay with this owner until a rule sends them to a child.
        </Typography>
      )}

      {rows.map((row, index) => (
        <Box key={index} sx={{display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1}}>
          <FormControl sx={{minWidth: 170}} size='small'>
            <InputLabel>Match on</InputLabel>
            <Select
              label='Match on'
              value={row.match}
              onChange={(e) => updateRow(index, {match: e.target.value, value: ''})}
            >
              {matchOptions.map((type) => (
                <MenuItem key={type} value={type}>{Helpers.MATCH_LABELS[type]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title={Helpers.VALUELESS_MATCHES.includes(row.match)
            ? (row.match === 'pen'
              ? 'Pen rules take no value: which pens belong to the child comes from the pen dimension'
              : 'Reassign takes no value: every row of the stream goes to the owner named here')
            : ''}>
            <TextField
              label={Helpers.VALUE_LABELS[row.match] || 'Value'}
              size='small'
              sx={{flex: 1}}
              value={Helpers.VALUELESS_MATCHES.includes(row.match) ? '' : row.value}
              disabled={Helpers.VALUELESS_MATCHES.includes(row.match)}
              placeholder={Helpers.VALUE_PLACEHOLDERS[row.match] || ''}
              onChange={(e) => updateRow(index, {value: e.target.value})}
            />
          </Tooltip>

          {anyOwner ? (
            <Autocomplete
              size='small'
              sx={{minWidth: 260}}
              options={ownerOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, selected) => option.id === selected.id}
              value={ownerOptions.find((o) => o.id === row.owner_id) || null}
              onChange={(event, option) => updateRow(index, {owner_id: option ? option.id : ''})}
              renderInput={(params) => <TextField {...params} label='Send to owner'/>}
            />
          ) : (
            <FormControl sx={{minWidth: 220}} size='small'>
              <InputLabel>Send to owner</InputLabel>
              <Select
                label='Send to owner'
                value={ownerOptions.some((o) => o.id === row.owner_id) ? row.owner_id : ''}
                onChange={(e) => updateRow(index, {owner_id: e.target.value})}
              >
                {ownerOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <IconButton aria-label='Remove rule' onClick={() => removeRow(index)} size='small'>
            <DeleteOutlineIcon fontSize='small'/>
          </IconButton>
        </Box>
      ))}

      {helperText && (
        <Typography variant='body2' color={error ? 'error' : 'textSecondary'} sx={{mt: 1}}>
          {helperText}
        </Typography>
      )}

      <Button size='small' onClick={() => setShowJson(!showJson)} sx={{mt: 1}}>
        {showJson ? 'Hide JSON' : 'Edit as JSON'}
      </Button>
      <Collapse in={showJson}>
        <TextField
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : (value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          multiline
          rows={6}
          variant='outlined'
          sx={{mt: 1}}
        />
      </Collapse>
    </Box>
  );
};

PartitionConfigsEditor.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  childOwners: PropTypes.array,
  owners: PropTypes.array,
  anyOwner: PropTypes.bool,
  providerScheme: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

PartitionConfigsEditor.defaultProps = {
  value: '',
  childOwners: [],
  owners: [],
  anyOwner: false,
  providerScheme: null,
  error: false,
  helperText: '',
};

export default PartitionConfigsEditor;
